/**
 * Glasnu marketing pop-up.
 *
 * Toont de pop-up één keer per bezoeker per campagne: sluiten (of doorklikken)
 * zet een vervaldatum in localStorage, en tot die datum blijft hij weg. De
 * campagnecode zit in de opslagsleutel, dus een nieuwe actie begint schoon.
 *
 * In de Theme Editor negeren we opslag en vertraging — anders kan een merchant
 * de pop-up nooit bekijken terwijl hij hem instelt.
 *
 * Zonder JavaScript blijft het element `hidden`; de pop-up kan de winkel dus
 * nooit blokkeren.
 */
class GlasnuPromoPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('.gn-promo-popup__dialog');
    this.storageKey = this.dataset.key;
    this.designMode = Boolean(window.Shopify && window.Shopify.designMode);

    if (!this.designMode && this.wasDismissed()) return;

    this.onKeydown = this.onKeydown.bind(this);

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-promo-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      const code = event.target.closest('[data-promo-code]');
      if (code) {
        this.copyCode(code);
        return;
      }

      // Doorklikken telt als gezien: de bezoeker heeft de actie gehad.
      if (event.target.closest('[data-promo-link], .gn-promo-popup__cta')) {
        this.remember();
      }
    });

    const delay = this.designMode ? 0 : Number(this.dataset.delay) || 0;
    this.timer = setTimeout(() => this.open(), delay);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    document.removeEventListener('keydown', this.onKeydown);
    document.documentElement.classList.remove('gn-scroll-locked');
  }

  open() {
    this.lastFocused = document.activeElement;
    this.removeAttribute('hidden');

    // Eén frame wachten zodat de transitie van [hidden] naar zichtbaar loopt.
    requestAnimationFrame(() => this.classList.add('is-open'));

    document.documentElement.classList.add('gn-scroll-locked');
    document.addEventListener('keydown', this.onKeydown);

    const focusTarget = this.querySelector('.gn-promo-popup__close');
    if (focusTarget) focusTarget.focus({ preventScroll: true });
  }

  close() {
    clearTimeout(this.timer);
    this.classList.remove('is-open');
    document.removeEventListener('keydown', this.onKeydown);
    document.documentElement.classList.remove('gn-scroll-locked');
    this.remember();

    const hide = () => {
      this.setAttribute('hidden', '');
      if (this.lastFocused && this.lastFocused.focus) {
        this.lastFocused.focus({ preventScroll: true });
      }
    };

    // Verbergen ná de uittredende transitie; als die er niet is (reduced
    // motion) vangt de timeout het af.
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      hide();
    };
    this.dialog.addEventListener('transitionend', finish, { once: true });
    setTimeout(finish, 400);
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key === 'Tab') this.trapFocus(event);
  }

  /* Focus blijft binnen de dialog zolang hij open staat. */
  trapFocus(event) {
    const focusable = this.dialog.querySelectorAll(
      'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  copyCode(button) {
    const hint = button.querySelector('[data-promo-code-hint]');
    const code = button.dataset.promoCode;
    if (!navigator.clipboard || !code) return;

    navigator.clipboard.writeText(code).then(() => {
      if (!hint) return;
      hint.textContent = button.dataset.copiedLabel || hint.textContent;
      const previous = button.dataset.copyLabel;
      button.classList.add('is-copied');
      setTimeout(() => {
        if (previous) hint.textContent = previous;
        button.classList.remove('is-copied');
      }, 2000);
    });
  }

  wasDismissed() {
    const until = Number(this.read());
    return Boolean(until) && Date.now() < until;
  }

  remember() {
    if (this.designMode) return;
    const days = Number(this.dataset.days) || 14;
    this.write(String(Date.now() + days * 86400000));
  }

  /* localStorage kan gooien (privémodus, geblokkeerde opslag). De pop-up
     werkt dan nog steeds, alleen zonder geheugen. */
  read() {
    try {
      return window.localStorage.getItem(this.storageKey);
    } catch (error) {
      return null;
    }
  }

  write(value) {
    try {
      window.localStorage.setItem(this.storageKey, value);
    } catch (error) {
      /* geen opslag beschikbaar */
    }
  }
}

if (!customElements.get('glasnu-promo-popup')) {
  customElements.define('glasnu-promo-popup', GlasnuPromoPopup);
}
